import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { cn } from "@/lib/utils";
import { setProjectPhaseReason } from "@/api/actions/project-log";
import { MAX_SCHEDULE_REASON } from "@buildpy/core/modules/project/phase-schedule";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

interface Props {
  projectId: string;
  phaseId: string;
  phaseName: string;
  reason: string;
  // 고칠 수 있는지 — 서버가 현장 상태(착공했고 안 닫힘)로 정해 넘긴다.
  editable: boolean;
  className?: string;
  // testid 앞부분 — 표와 폰 카드가 한 화면에 같이 있을 때 카드 쪽이 겹치지 않게 바꾼다.
  testIdPrefix?: string;
}

// 계획과 다른 이유 — 보이기만 하거나, 눌러서 나중에 적고 고친다.
const PhaseReasonEditor = ({ projectId, phaseId, phaseName, reason, editable, className, testIdPrefix }: Props) => {
  const tid = testIdPrefix ?? `phase-${phaseId}`;
  const { pending, run } = useRecordAction();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reason);

  const save = () =>
    void run(
      "사유를 저장하지 못했습니다",
      () => setProjectPhaseReason(projectId, { phase_id: phaseId, reason: draft.trim() }),
      () => {
        setEditing(false);
        toast.success(`${phaseName} — 사유 저장`);
      }
    );

  if (editing) {
    return (
      <div className={cn("flex min-w-0 flex-wrap items-center gap-1", className)} data-testid={`${tid}-reason-edit`}>
        <div className="min-w-40 flex-1">
          <Input
            aria-label={`${phaseName} 계획과 다른 이유`}
            disabled={pending}
            inputSize="xs"
            maxLength={MAX_SCHEDULE_REASON}
            name={`phase-${phaseId}-reason-edit`}
            placeholder="계획과 다른 이유"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <Button disabled={pending} size="sm" type="button" onClick={save}>
          저장
        </Button>
        <Button
          disabled={pending}
          size="sm"
          type="button"
          variant="ghost"
          onClick={() => {
            setDraft(reason);
            setEditing(false);
          }}>
          취소
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-wrap items-baseline gap-x-2 text-xs", className)} data-testid={`${tid}-reason`}>
      {reason && <span className="break-words text-muted-foreground">{reason}</span>}
      {editable && (
        <Button
          size="xs-touch"
          type="button"
          variant="link"
          onClick={() => {
            setDraft(reason);
            setEditing(true);
          }}>
          {reason ? "사유 고치기" : "사유 적기"}
        </Button>
      )}
    </div>
  );
};

export default PhaseReasonEditor;
